import React, { useState, useEffect, useRef, useCallback } from 'react';
import Draggable from 'react-draggable';
import ReactDOM from 'react-dom';
import { Send, ChevronDown, Minus } from 'lucide-react';
import axios from 'axios';
import { renderChadMarkdown } from './chadMarkdown';
import { ActionCard } from './ActionCard';
import chadAvatar from '../../assets/ai-chad-avatar.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS_KEY = 'ai-chad-position';

export default function AiChad({ currentRoute, activeProjectId, darkMode }) {
  const [expanded, setExpanded] = useState(false);
  const [sleeping, setSleeping] = useState(() => {
    try { return localStorage.getItem('ai-chad-sleeping') === 'true'; } catch { return false; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const avatarRef = useRef(null);
  const inputRef = useRef(null);
  const [facingLeft, setFacingLeft] = useState(false);
  const lastDragXRef = useRef(null);
  // DP80 Part 2 — the narrow-viewport resting position was decided from
  // window.innerWidth at render time only, so a rotate or a resize left him
  // in the position for the width he was FIRST drawn at. Tracked as state so
  // the crossing of 1024 actually moves him.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  // PH-1 — below 640 he lives in the header (shell-ai-chad); the floating avatar shows only with the panel open.
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const onResize = () => { setIsNarrow(window.innerWidth < 1024); setIsPhone(window.innerWidth < 640); };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const getSavedOffset = () => {
    try {
      const s = localStorage.getItem(POS_KEY);
      if (s) return JSON.parse(s);
    } catch {}
    return { x: 0, y: 0 };
  };

  const greetedKeyRef = useRef(null);

  useEffect(() => {
    if (!activeProjectId) return undefined;
    const key = `${activeProjectId}::${currentRoute}`;
    if (key === greetedKeyRef.current) return undefined;
    greetedKeyRef.current = key;
    // SL-1b — give the page's own data calls the loop first; skip while the
    // tab is hidden so a shadow request can't stall the next real one.
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      axios
        .get(`${API}/ai-chad/greeting`, { params: { project_id: activeProjectId, screen: currentRoute || 'dashboard' } })
        .then(r => { if (!cancelled) setMessages(prev => [{ role: 'assistant', content: r.data.greeting }, ...prev]); })
        .catch(() => {});
    }, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [activeProjectId, currentRoute]);

  useEffect(() => {
    if (chatEndRef.current && expanded) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, expanded]);

  useEffect(() => {
    if (expanded && inputRef.current) setTimeout(() => inputRef.current?.focus(), 150);
  }, [expanded]);

  // The project desk's AI Chad strip and the Site Conditions question chips
  // raise 'open-ai-chad' with a prefill; this is the one listener for it.
  useEffect(() => {
    const onOpen = (e) => {
      // PH-1 — on a page with the greeting card, a prefilled ask stays in the card, as on the desk.
      if (e?.detail?.prefill && document.querySelector('[data-testid="ai-chad-greeting-input"]')) return;
      setSleeping(false);
      try { localStorage.setItem('ai-chad-sleeping', 'false'); } catch { /* private mode */ }
      setExpanded(true);
      const q = e?.detail?.prefill;
      if (q) {
        setInput(q);
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    };
    window.addEventListener('open-ai-chad', onOpen);
    return () => window.removeEventListener('open-ai-chad', onOpen);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const onEsc = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [expanded]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const history = messages.slice(-30).filter(m => !m.hidden)
        .map(m => ({ role: m.role, content: m.content, screen: currentRoute || 'dashboard' }));
      const res = await axios.post(`${API}/ai-chad/chat`, {
        message: text,
        project_id: activeProjectId || null,
        current_screen: currentRoute || 'dashboard',
        conversation_history: history,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        proposed_actions: Array.isArray(res.data.proposed_actions) ? res.data.proposed_actions : [],
      }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Something went sideways. Try again in a sec." }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, activeProjectId, currentRoute]);

  const onKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const onDragStop = (_e, d) => { localStorage.setItem(POS_KEY, JSON.stringify({ x: d.x, y: d.y })); lastDragXRef.current = null; };
  const onDrag = (_e, d) => {
    if (lastDragXRef.current !== null) {
      if (d.x < lastDragXRef.current) setFacingLeft(true);
      else if (d.x > lastDragXRef.current) setFacingLeft(false);
    }
    lastDragXRef.current = d.x;
  };
  const goSleep = () => { setSleeping(true); setExpanded(false); localStorage.setItem('ai-chad-sleeping', 'true'); };
  const wake = () => { setSleeping(false); localStorage.setItem('ai-chad-sleeping', 'false'); };

  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
  const pill = lastMsg
    ? (lastMsg.content.length > 20 ? lastMsg.content.slice(0, 20) + '...' : lastMsg.content)
    : 'Ask AI Chad...';

  function getAvatarStyle() {
    if (sleeping) return { filter: 'saturate(0.4) brightness(0.8)', transform: 'rotate(15deg) scale(0.9)', transition: 'all 0.5s ease' };
    if (loading) return { filter: 'brightness(1.05) saturate(1.2) hue-rotate(5deg)', transition: 'all 0.3s ease' };
    const justOpened = expanded && messages.length <= 1;
    if (justOpened) return { filter: 'brightness(1.08) saturate(1.15)', transform: 'scale(1.05)', transition: 'all 0.4s ease' };
    return { filter: 'none', transition: 'all 0.5s ease' };
  }
  const avatarExpr = getAvatarStyle();

  // DP101 ruling 3 — HE WAS A DEAD REGION. DP100 measured it: a wheel over him
  // left the panel at 200, exactly the fault the masthead band had before DP95,
  // and he floats over the page on the Work Table. The wheel goes to the panel,
  // unless it is over something of his own that scrolls — his message list.
  const forwardWheel = (event) => {
    const panel = document.querySelector('.km-panel');
    if (!panel) return;
    let node = event.target;
    while (node && node !== event.currentTarget) {
      if (node.scrollHeight > node.clientHeight + 4) return;
      node = node.parentElement;
    }
    panel.scrollTop += event.deltaY;
  };


  const el = (
    <>
      {/* ===== AVATAR + PILL (pill sits under avatar) ===== */}
      {!(isPhone && !expanded) && (
      <Draggable nodeRef={avatarRef} defaultPosition={getSavedOffset()} onStop={onDragStop} onDrag={onDrag} handle=".chad-handle">
        <div
          ref={avatarRef}
          /* DP70 Part 3 — THE RESTING POSITION ONLY.
             Was `bottom: calc(33vh - 28px), left: 16`, which is a fraction of
             the viewport: at 1080 it put his box at y643-752 and at 844 it put
             it at y485-594 — mid-page, and 24px into the content region beside
             a collapsed 72px rail.
             Now a fixed foot anchor. The rail's foot boxes measure y896 (dark
             mode) and y944 (admin tools) in a 1080 viewport, i.e. 184px up from
             the bottom; 196 clears the highest of them by 12px. His box is 109
             tall, so he rests at y775-884 — inside the rail column's empty
             lower band (the collapsed groups end at y497), above the fold, and
             clear of the first sheet at x96.
             He still floats: Draggable's saved offset is applied on top of this
             and is untouched. */
          /* DP80 Part 2 — at 1920 he rests at bottom 196, left 8 with zero
             overlaps (DP70) and that does not change. Below 1024 that spot is
             the Yard's 48px left toolbar, where he covered Upload and Comment,
             so he moves to bottom 8, left 60: clear of the toolbar (0-48) and
             clear of the drawer's pill (271-378). He still floats and still
             drags — Draggable's saved offset applies on top of this. */
          style={isNarrow
            ? { position: 'fixed', bottom: 8, left: 60, zIndex: 'var(--km-z-aichad)' }
            : { position: 'fixed', bottom: 196, left: 8, zIndex: 'var(--km-z-aichad)' }}
          onWheel={forwardWheel}
          data-testid="ai-chad-container"
        >
          <div className="chad-handle" style={{ cursor: 'grab' }}>
            <div
              onClick={() => sleeping ? wake() : setExpanded(p => !p)}
              style={{ cursor: 'pointer', position: 'relative', opacity: sleeping ? 0.5 : 1, transition: 'opacity 0.3s' }}
              data-testid="ai-chad-avatar"
            >
              {/* Zzz when sleeping */}
              {sleeping && (
                <span style={{ position: 'absolute', top: -14, right: 0, fontSize: 12, color: '#BF7F3F', fontWeight: 700, opacity: 1, animation: 'chadFadeIn 0.5s ease', zIndex: 1, pointerEvents: 'none' }}>Zzz</span>
              )}
              {/* Outer: direction flip */}
              <div style={{ transform: facingLeft ? 'scaleX(-1)' : 'none', transition: 'transform 0.2s ease' }}>
                {/* Inner: breathing animation + expression filters */}
                <img
                  src={chadAvatar}
                  alt="AI Chad"
                  draggable={false}
                  className={loading ? 'chad-pulse' : 'chad-breathe'}
                  style={{ width: sleeping ? 48 : 80, height: 'auto', pointerEvents: 'none', transition: 'width 0.3s', ...avatarExpr }}
                />
              </div>
              <div
                style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 12, height: 12, borderRadius: '50%',
                  backgroundColor: loading ? '#BF7F3F' : '#22c55e',
                  border: '2px solid #fff',
                  animation: loading ? 'chadStatusPulse 1.2s ease-in-out infinite' : 'none',
                }}
                data-testid="ai-chad-status-dot"
              />
            </div>
          </div>

          {/* Pill — centered under avatar */}
          {!expanded && !sleeping && (
            <div
              style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}
            >
              <div
                onClick={() => setExpanded(true)}
                style={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 10,
                  padding: '3px 8px',
                  cursor: 'pointer',
                  boxShadow: 'var(--card-shadow)',
                  maxWidth: 80,
                  overflow: 'hidden',
                }}
                data-testid="ai-chad-pill"
              >
              <p style={{
                color: 'hsl(var(--muted-foreground))', fontSize: 11, margin: 0,
                lineHeight: 1.3, textAlign: 'center', whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {pill}
              </p>
            </div>
            </div>
          )}
        </div>
      </Draggable>
      )}

      {/* ===== BOTTOM DRAWER — fades in, mode-aware colors, visible border ===== */}
      {expanded && !sleeping && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 16,
            // DP80 Part 2 — the open panel was a fixed 420px at left 16, which
            // is 436px inside a 390px phone: 46px of it sat off-screen and
            // took the send button with it. Below 1024 it fits the viewport.
            width: isNarrow ? 'calc(100vw - 32px)' : 420,
            maxWidth: 'calc(100vw - 32px)',
            minHeight: 180,
            maxHeight: 260,
            zIndex: 'var(--km-z-aichad)',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'hsl(var(--card))',
            border: '1.5px solid hsl(var(--border))',
            borderBottom: 'none',
            boxShadow: 'var(--card-shadow-lg)',
            animation: 'chadFadeIn 0.3s ease-out',
          }}
          data-testid="ai-chad-panel"
        >
          {/* Drag handle bar */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 0' }}>
            <div style={{ width: 32, height: 4, borderRadius: 2, background: 'hsl(var(--border))' }} />
          </div>

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 18px 8px',
          }}>
            <span style={{ color: '#BC5D41', fontWeight: 700, fontSize: 14, letterSpacing: '0.03em' }}>AI Chad</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={goSleep}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', padding: 4, display: 'flex', borderRadius: 6 }}
                title="Sleep"
                data-testid="ai-chad-sleep-btn"
              >
                <Minus size={16} />
              </button>
              <button
                onClick={() => setExpanded(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', padding: 4, display: 'flex', borderRadius: 6 }}
                data-testid="ai-chad-collapse-btn"
              >
                <ChevronDown size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1, overflowY: 'auto', padding: '4px 18px 10px',
              display: 'flex', flexDirection: 'column', gap: 10,
              maxHeight: 140, minHeight: 40,
            }}
            data-testid="ai-chad-messages"
          >
            {messages.length === 0 && (
              <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 13, textAlign: 'center', margin: 'auto', fontStyle: 'italic' }}>
                What's on your mind?
              </p>
            )}
            {messages.filter(m => !m.hidden).map((m, i) => (
              <React.Fragment key={i}>
              <div
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '70%', padding: '8px 14px',
                  borderRadius: m.role === 'user' ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                  background: m.role === 'user' ? '#BC5D41' : 'hsl(var(--muted))',
                  color: m.role === 'user' ? '#fff' : 'hsl(var(--card-foreground))',
                  fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
                data-testid={`ai-chad-msg-${m.role}-${i}`}
              >
                {m.role === 'assistant' ? renderChadMarkdown(m.content) : m.content}
              </div>
              {(m.proposed_actions || []).map((p) => (
                <ActionCard
                  key={p.id}
                  proposal={p}
                  onDone={(taken) => setMessages(prev => [...prev, {
                    role: 'user',
                    content: `actions_taken: ${JSON.stringify([taken])}`,
                    hidden: true,
                  }])}
                />
              ))}
              </React.Fragment>
            ))}
            {loading && (
              <div style={{
                alignSelf: 'flex-start', padding: '8px 14px', borderRadius: '14px 14px 14px 2px',
                background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))', fontSize: 13,
              }}>
                <span className="animate-pulse">Thinking...</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px 14px',
            borderTop: '1px solid hsl(var(--border))',
            background: 'hsl(var(--background))',
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask something..."
              style={{
                flex: 1,
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 10,
                padding: '10px 14px',
                color: 'hsl(var(--foreground))',
                fontSize: 13, outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = '#BC5D41'; }}
              onBlur={e => { e.target.style.borderColor = ''; }}
              data-testid="ai-chad-input"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              style={{
                background: input.trim() ? '#BC5D41' : 'hsl(var(--muted))',
                border: 'none', borderRadius: 10,
                width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: input.trim() ? 'pointer' : 'default', flexShrink: 0,
              }}
              data-testid="ai-chad-send-btn"
            >
              <Send size={16} color="#fff" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chadFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes chadStatusPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes chadBreathe {
          0%, 100% { transform: scaleY(1) translateY(0px); }
          50% { transform: scaleY(1.02) translateY(-2px); }
        }
        @keyframes chadActivePulse {
          0%, 100% { transform: scaleY(1) translateY(0px); }
          50% { transform: scaleY(1.04) translateY(-3px); }
        }
        .chad-breathe { animation: chadBreathe 3s ease-in-out infinite; }
        .chad-pulse { animation: chadActivePulse 1s ease-in-out infinite; }
      `}</style>
    </>
  );

  return ReactDOM.createPortal(el, document.body);
}
